import React, { useState, useRef, useEffect, useContext } from 'react';
import {
    arrow, autoUpdate, flip, FloatingPortal, hide, offset, safePolygon, shift,
    useDismiss, useFloating, useFocus, useHover, useInteractions, useMergeRefs, useRole,
} from '@floating-ui/react';
import { ModalPopupContext } from 'src/components/common/Modals/Modal/ModalPopupContext';
import './Tooltip.scss';

interface TooltipProps {
    content?: string;
    children: React.ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right';
    className?: string;
    disabled?: boolean;
    /** Attach to a single element that forwards DOM props and its ref. Disabled controls use a wrapper. */
    asChild?: boolean;
}

type TriggerProps = React.HTMLAttributes<HTMLElement> & {
    ref?: React.Ref<HTMLElement>;
    disabled?: boolean;
};

export const Tooltip: React.FC<TooltipProps> = ({
    content, children, position = 'top', className = '', disabled = false, asChild = false,
}) => {
    const enabled = !disabled && !!content;
    const [isOpen, setIsOpen] = useState(false);
    const open = enabled && isOpen;
    const arrowRef = useRef<HTMLDivElement>(null);
    const owner = useRef({});
    const modal = useContext(ModalPopupContext);
    const child = asChild ? React.Children.only(children) as React.ReactElement<TriggerProps> : null;
    const attachToChild = !!child && !child.props.disabled;
    const { refs, floatingStyles, context, placement, middlewareData, isPositioned } = useFloating({
        open,
        onOpenChange: setIsOpen,
        placement: position,
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [offset(8), flip({ padding: 8, fallbackAxisSideDirection: 'end' }),
            shift({ padding: 8, crossAxis: true }),
            arrow({ element: arrowRef, padding: 6 }), hide({ strategy: 'referenceHidden' })],
    });
    const hover = useHover(context, {
        enabled, move: false, mouseOnly: true, handleClose: safePolygon({ blockPointerEvents: false }),
    });
    const focus = useFocus(context, { enabled, visibleOnly: false });
    const dismiss = useDismiss(context, { enabled });
    const role = useRole(context, { enabled, role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
    const mergedRef = useMergeRefs([refs.setReference, attachToChild ? child.props.ref : undefined]);

    useEffect(() => {
        if (!enabled) setIsOpen(false);
    }, [enabled]);

    useEffect(() => {
        if (!open || !modal) return;
        const token = owner.current;
        modal.setOpen(token, true, () => setIsOpen(false));
        return () => modal.setOpen(token, false);
    }, [open, modal]);

    const side = placement.split('-')[0];
    const { 'aria-describedby': existingDescription, ...childProps } = attachToChild ? child.props : {};
    const referenceProps = getReferenceProps(childProps);
    const describedBy = [
        existingDescription,
        referenceProps['aria-describedby'],
    ].filter(Boolean).join(' ') || undefined;
    const trigger = attachToChild
        ? React.cloneElement(child, {
            ...referenceProps,
            ref: mergedRef,
            className: [child.props.className, 'custom-tooltip-target', className].filter(Boolean).join(' '),
            'aria-describedby': describedBy ? [...new Set(describedBy.split(/\s+/))].join(' ') : undefined,
        })
        : <span
            {...referenceProps}
            ref={refs.setReference}
            className={`custom-tooltip-trigger ${className}`}
            tabIndex={enabled ? 0 : undefined}
            aria-label={child?.props['aria-label']}
        >
            {children}
        </span>;

    return <>
        {trigger}
        {open && (!modal || modal.host) && (
            <FloatingPortal root={modal?.host}>
                <div
                    {...getFloatingProps()}
                    ref={refs.setFloating}
                    className={`custom-tooltip custom-tooltip--${side}`}
                    style={{
                        ...floatingStyles,
                        visibility: !isPositioned || middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
                    }}
                >
                    {content}
                    <div
                        ref={arrowRef}
                        aria-hidden="true"
                        className={`custom-tooltip__arrow custom-tooltip__arrow--${side}`}
                        style={{ left: middlewareData.arrow?.x, top: middlewareData.arrow?.y }}
                    />
                </div>
            </FloatingPortal>
        )}
    </>;
};
